import { useState, useEffect, useRef } from 'react';
import ConnectionSlotsCard from '@/components/ConnectionSlotsCard';
import { useConnectionsStore, useAuthStore, api } from '../store';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Wifi, WifiOff, Trash2, RefreshCw, Loader2, Phone, Filter, Settings, QrCode, PhoneOff } from 'lucide-react';

export default function ConnectionsPage() {
  const { connections, fetchConnections, createConnection, deleteConnection, loading } = useConnectionsStore();
  const { user } = useAuthStore();
  const [newConnectionName, setNewConnectionName] = useState('');
  const [connectionType, setConnectionType] = useState('uazapi');
  const [cloudApiToken, setCloudApiToken] = useState('');
  const [cloudApiPhoneId, setCloudApiPhoneId] = useState('');
  const [showCloudInfo, setShowCloudInfo] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [billing, setBilling] = useState(null);
  useEffect(() => {
    if (user?.role === 'admin') return;
    api.get('/connections/billing').then(r => setBilling(r.data)).catch(() => {});
  }, [user, connections.length]);
  const [deletingId, setDeletingId] = useState(null);
  const [ownerFilter, setOwnerFilter] = useState('mine');
  
  const isAdmin = user?.role === 'admin';
  
  // Estado para QR codes - cada conexão pode ter seu próprio QR
  const [qrStates, setQrStates] = useState({});
  const pollingRefs = useRef({});

  // Estado para modal de configurações
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [connectionSettings, setConnectionSettings] = useState({
    rejectCall: false,
    msgCall: '',
  });
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // Estado para modal de conexão (QR Code)
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [connectingConnection, setConnectingConnection] = useState(null);

  useEffect(() => {
    fetchConnections(false, ownerFilter);
    return () => {
      // Limpar todos os pollings ao desmontar
      Object.values(pollingRefs.current).forEach(clearInterval);
    };
  }, [ownerFilter]);

  // Função para buscar QR de uma conexão específica
  const fetchQR = async (connectionId) => {
    try {
      const token = localStorage.getItem('nexus-token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connections/${connectionId}/qr`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      setQrStates(prev => ({
        ...prev,
        [connectionId]: {
          ...prev[connectionId],
          qrImage: data.qrImage,
          status: data.status,
          phoneNumber: data.phoneNumber,
          loading: false
        }
      }));

      // Se conectou, para o polling e atualiza lista
      if (data.status === 'connected') {
        stopPolling(connectionId);
        fetchConnections(false, ownerFilter);
        toast.success('WhatsApp conectado!');
      }
    } catch (error) {
      console.error('Erro ao buscar QR:', error);
    }
  };

  // Iniciar polling para uma conexão
  const startPolling = (connectionId) => {
    if (pollingRefs.current[connectionId]) {
      clearInterval(pollingRefs.current[connectionId]);
    }
    
    // Busca imediata
    fetchQR(connectionId);
    
    // Polling a cada 2 segundos
    pollingRefs.current[connectionId] = setInterval(() => {
      fetchQR(connectionId);
    }, 2000);
  };

  // Parar polling
  const stopPolling = (connectionId) => {
    if (pollingRefs.current[connectionId]) {
      clearInterval(pollingRefs.current[connectionId]);
      delete pollingRefs.current[connectionId];
    }
  };

  // Cancelar conexão (parar de mostrar QR)
  const handleCancelConnect = (connectionId) => {
    stopPolling(connectionId);
    setQrStates(prev => {
      const newState = { ...prev };
      delete newState[connectionId];
      return newState;
    });
    // Fechar modal e resetar estados
    setConnectModalOpen(false);
    setConnectingConnection(null);
  };

  // Abrir modal e iniciar QR Code automaticamente
  const handleOpenConnectModal = async (connection) => {
    setConnectingConnection(connection);
    setConnectModalOpen(true);
    
    // Iniciar conexão QR automaticamente
    const connectionId = connection.id;
    setQrStates(prev => ({
      ...prev,
      [connectionId]: { loading: true, qrImage: null, status: 'connecting', showLoader: false }
    }));

    const loaderTimeout = setTimeout(() => {
      setQrStates(prev => ({
        ...prev,
        [connectionId]: { ...prev[connectionId], showLoader: true }
      }));
    }, 300);

    try {
      const token = localStorage.getItem('nexus-token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connections/${connectionId}/connect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      startPolling(connectionId);
    } catch (error) {
      clearTimeout(loaderTimeout);
      toast.error('Erro ao iniciar conexão');
      setQrStates(prev => ({
        ...prev,
        [connectionId]: { loading: false, status: 'error', showLoader: false }
      }));
    }
  };

  // Conectar WhatsApp - abre modal com QR Code
  const handleConnect = (connection) => {
    handleOpenConnectModal(connection);
  };

  // Desconectar WhatsApp
  const handleDisconnect = async (connectionId) => {
    try {
      const token = localStorage.getItem('nexus-token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connections/${connectionId}/disconnect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Desconectado');
      fetchConnections(false, ownerFilter);
    } catch (error) {
      toast.error('Erro ao desconectar');
    }
  };

  // Criar nova conexão
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newConnectionName.trim()) return;
    
    if (connectionType === 'cloud_api') {
      if (!cloudApiToken.trim() || !cloudApiPhoneId.trim()) {
        toast.error('Token e Phone Number ID são obrigatórios para API Oficial');
        return;
      }
    }
    
    setCreating(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const body = {
        name: newConnectionName.trim(),
        connection_type: connectionType,
      };
      if (connectionType === 'cloud_api') {
        body.cloud_api_token = cloudApiToken.trim();
        body.cloud_api_phone_id = cloudApiPhoneId.trim();
      }
      
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connections`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      if (res.ok) {
        setNewConnectionName('');
        setCloudApiToken('');
        setCloudApiPhoneId('');
        setConnectionType('uazapi');
        setShowNewForm(false);
        toast.success('Conexão criada com sucesso!');
        fetchConnections(false, ownerFilter);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erro ao criar');
      }
    } catch (error) {
      toast.error('Erro ao criar conexão');
    } finally {
      setCreating(false);
    }
  };

  // Deletar conexão
  const handleDelete = async (connectionId) => {
    setDeletingId(connectionId);
    stopPolling(connectionId);
    try {
      await deleteConnection(connectionId);
      toast.success('Conexão deletada');
    } catch (error) {
      toast.error('Erro ao deletar');
    } finally {
      setDeletingId(null);
    }
  };

  // Sincronizar grupos e perfil
  const handleSyncGroups = async (connectionId) => {
    try {
      const token = localStorage.getItem('nexus-token');
      toast.loading('Sincronizando...');
      console.log(`[DEBUG] Sincronizando para: ${connectionId}`);
      
      // Primeiro sincronizar o perfil (foto, nome, telefone)
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connections/${connectionId}/sync-profile`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
        console.log('[DEBUG] Erro ao sincronizar perfil:', e);
      }
      
      // Depois sincronizar grupos
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connections/${connectionId}/refresh-groups`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.dismiss();
      
      const data = await response.json();
      console.log(`[DEBUG] Resposta sync grupos:`, data);
      
      if (response.ok) {
        toast.success(`${data.count || 0} grupos sincronizados!`);
        if (data.count === 0) {
          toast.warning('Nenhum grupo encontrado. Verifique se a conexão está ativa.');
        }
      } else {
        console.error(`[DEBUG] Erro ao sincronizar:`, data);
        // Atualizar lista para mostrar status real
        fetchConnections(false, ownerFilter);
        toast.error(data.detail || 'Erro ao sincronizar');
      }
      
      fetchConnections(false, ownerFilter);
    } catch (error) {
      toast.dismiss();
      toast.error('Erro ao sincronizar');
    }
  };

  // Abrir modal de configurações
  const handleOpenSettings = async (connection) => {
    setSelectedConnection(connection);
    setSettingsModalOpen(true);
    setLoadingSettings(true);
    
    try {
      const token = localStorage.getItem('nexus-token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connections/${connection.id}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConnectionSettings({
          rejectCall: data.rejectCall || false,
          msgCall: data.msgCall || '',
        });
      } else {
        // Se a conexão não está sincronizada com a UAZAPI
        toast.error('Erro ao carregar configurações');
        setSettingsModalOpen(false);
      }
    } catch (error) {
      console.error('Erro ao buscar configurações:', error);
      toast.error('Erro ao carregar configurações');
      setSettingsModalOpen(false);
    } finally {
      setLoadingSettings(false);
    }
  };

  // Salvar configurações
  const handleSaveSettings = async () => {
    if (!selectedConnection) return;
    
    setSavingSettings(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connections/${selectedConnection.id}/settings`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(connectionSettings)
      });
      
      if (response.ok) {
        toast.success('Configurações salvas com sucesso!');
        setSettingsModalOpen(false);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao salvar configurações');
      }
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSavingSettings(false);
    }
  };

  const limiteTotal = billing ? (billing.limite_total === -1 ? '∞' : billing.limite_total) : (user?.max_connections || 1);
  const maxConnections = user?.role === 'admin' ? '∞' : limiteTotal;
  const canCreateMore = user?.role === 'admin' || (typeof limiteTotal === 'number' && connections.length < limiteTotal);

  const getStatusBadge = (connection) => {
    const qrState = qrStates[connection.id];
    // PRIORIDADE: Se o banco diz que está conectado, confiar no banco
    const status = connection.status === 'connected' ? 'connected' : (qrState?.status || connection.status);
    
    switch (status) {
      case 'connected':
        return <Badge className="bg-green-500">Conectado</Badge>;
      case 'connecting':
      case 'waiting_qr':
        return <Badge className="bg-yellow-500">Aguardando QR</Badge>;
      case 'disconnected':
        return <Badge variant="secondary">Desconectado</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <style>{`@keyframes anim-fade{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}.anim-fade-in{animation:anim-fade .3s ease both}`}</style>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Conexões WhatsApp</h1>
          <p className="text-muted-foreground">
            {connections.length} de {maxConnections} conexões
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Admin Filter */}
          {isAdmin && (
            <Select value={ownerFilter} onValueChange={setOwnerFilter}>
              <SelectTrigger className="w-[180px] bg-muted/50 border-border">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrar por" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas do Sistema</SelectItem>
                <SelectItem value="mine">Apenas Minhas</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button onClick={() => fetchConnections(false, ownerFilter)} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {!isAdmin && <ConnectionSlotsCard />}

      {/* Formulário de criação */}
      {canCreateMore && (
        <Card>
          <CardContent className="pt-6">
            {/* Card clicável: criar nova conexão de WhatsApp */}
            {!showNewForm ? (
              <button
                type="button"
                onClick={() => setShowNewForm(true)}
                className="w-full group relative overflow-hidden rounded-2xl border-2 border-dashed border-green-500/30 hover:border-green-500/60 bg-gradient-to-br from-green-500/[0.04] to-transparent p-6 transition-all hover:shadow-lg"
              >
                <div className="pointer-events-none absolute -top-10 -right-10 w-32 h-32 rounded-full bg-green-500/10 blur-3xl group-hover:bg-green-500/20 transition-all" />
                <div className="relative flex flex-col items-center gap-3">
                  <div className="w-16 h-16 rounded-2xl bg-green-500/15 text-green-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Plus className="w-8 h-8" />
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-lg">Nova conexão de WhatsApp</p>
                    <p className="text-sm text-muted-foreground">Conecte um número para enviar cobranças, campanhas e usar a IA</p>
                  </div>
                </div>
              </button>
            ) : (
              <form onSubmit={handleCreate} className="space-y-4 anim-fade-in">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-green-500/15 text-green-500 flex items-center justify-center shrink-0"><Wifi className="w-5 h-5" /></div>
                  <div className="flex-1">
                    <Label className="text-sm">Dê um nome para a conexão</Label>
                    <Input
                      placeholder="Ex.: WhatsApp Vendas, Cobranças…"
                      value={newConnectionName}
                      onChange={(e) => setNewConnectionName(e.target.value)}
                      autoFocus
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => { setShowNewForm(false); setNewConnectionName(''); }}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={creating || !newConnectionName.trim()} className="flex-1 bg-green-600 hover:bg-green-700">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    Criar conexão
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      )}

      {/* Lista de conexões */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : connections.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Wifi className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Nenhuma conexão criada</p>
            <p className="text-sm">Crie sua primeira conexão acima</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {connections.map((connection) => {
            const qrState = qrStates[connection.id];
            const isConnecting = qrState?.loading || qrState?.status === 'connecting' || qrState?.status === 'waiting_qr';
            const isConnected = connection.status === 'connected' || qrState?.status === 'connected';
            const isDeleting = deletingId === connection.id;

            return (
              <Card key={connection.id} className={`relative overflow-hidden transition-all hover:shadow-lg ${isConnected ? 'border-green-500/30' : 'border-border/60'}`}>
                {/* faixa de status no topo */}
                <div className={`absolute top-0 left-0 right-0 h-1 ${isConnected ? 'bg-gradient-to-r from-green-500 to-emerald-400' : isConnecting ? 'bg-amber-400' : 'bg-border'}`} />
                <CardHeader className="pb-3 pt-5">
                  <div className="flex items-start gap-3">
                    {/* Foto de perfil do WhatsApp */}
                    {connection.profile_pic_url ? (
                      <img 
                        src={connection.profile_pic_url} 
                        alt="Perfil" 
                        className="w-14 h-14 rounded-2xl object-cover border-2 border-green-500/30 flex-shrink-0 shadow"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${isConnected ? 'bg-green-500/15' : 'bg-muted'}`}>
                        <Phone className={`w-6 h-6 ${isConnected ? 'text-green-500' : 'text-muted-foreground'}`} />
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <CardTitle className="text-lg truncate">{connection.name}</CardTitle>
                        {getStatusBadge(connection)}
                      </div>
                      {/* Owner badge for admin viewing all */}
                      {isAdmin && ownerFilter === 'all' && connection.owner_username && (
                        <Badge variant="outline" className="w-fit text-[10px] mt-1 bg-blue-500/10 text-blue-400 border-blue-500/20">
                          {connection.owner_username}
                        </Badge>
                      )}
                      {connection.phone_number && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <Phone className="w-3 h-3" />
                          {connection.phone_number}
                        </p>
                      )}
                      {connection.connection_type === 'cloud_api' && (
                        <Badge variant="outline" className="w-fit text-[10px] mt-1 bg-green-500/10 text-green-400 border-green-500/20">
                          API Oficial
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {/* Grupos */}
                  {isConnected && connection.groups_count > 0 && (
                    <p className="text-sm text-muted-foreground">
                      {connection.groups_count} grupos sincronizados
                    </p>
                  )}

                  {/* Botões de ação */}
                  <div className="flex gap-2 flex-wrap">
                    {!isConnected && !isConnecting && connection.connection_type !== 'cloud_api' && (
                      <Button 
                        size="sm" 
                        onClick={() => handleConnect(connection)}
                        className="flex-1"
                      >
                        <Wifi className="w-4 h-4 mr-1" />
                        Conectar
                      </Button>
                    )}
                    
                    {/* Webhook URL para Cloud API */}
                    {connection.connection_type === 'cloud_api' && isConnected && (
                      <div className="w-full space-y-1">
                        <Label className="text-[10px] text-muted-foreground">Webhook URL (configurar na Meta)</Label>
                        <div className="flex gap-1">
                          <Input 
                            readOnly 
                            value={`${window.location.origin}/api/webhook/cloud-api/${connection.id}`}
                            className="text-[10px] h-7 bg-muted/50"
                          />
                          <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/api/webhook/cloud-api/${connection.id}`);
                            toast.success('URL copiada!');
                          }}>
                            Copiar
                          </Button>
                        </div>
                        {connection.cloud_api_verify_token && (
                          <div className="flex gap-1">
                            <Input 
                              readOnly 
                              value={connection.cloud_api_verify_token}
                              className="text-[10px] h-7 bg-muted/50"
                            />
                            <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => {
                              navigator.clipboard.writeText(connection.cloud_api_verify_token);
                              toast.success('Verify Token copiado!');
                            }}>
                              Copiar
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {isConnected && connection.connection_type !== 'cloud_api' && (
                      <>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleOpenSettings(connection)}
                        >
                          <Settings className="w-4 h-4 mr-1" />
                          Config
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleSyncGroups(connection.id)}
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Sincronizar
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleDisconnect(connection.id)}
                        >
                          <WifiOff className="w-4 h-4 mr-1" />
                          Desconectar
                        </Button>
                      </>
                    )}
                    
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => handleDelete(connection.id)}
                      disabled={isDeleting}
                    >
                      {isDeleting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal de Configurações */}
      <Dialog open={settingsModalOpen} onOpenChange={setSettingsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PhoneOff className="w-5 h-5" />
              Configurações da Conexão
            </DialogTitle>
            <DialogDescription>
              {selectedConnection?.name}
            </DialogDescription>
          </DialogHeader>
          
          {loadingSettings ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {/* Recusar Ligação Automaticamente */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border">
                <div className="space-y-0.5">
                  <Label htmlFor="rejectCall" className="text-base font-medium flex items-center gap-2">
                    <PhoneOff className="w-4 h-4 text-red-500" />
                    Recusar Ligação Automaticamente
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Rejeita automaticamente todas as chamadas recebidas
                  </p>
                </div>
                <Switch
                  id="rejectCall"
                  checked={connectionSettings.rejectCall}
                  onCheckedChange={(checked) => setConnectionSettings(prev => ({
                    ...prev,
                    rejectCall: checked
                  }))}
                />
              </div>

              {/* Mensagem Personalizada - só aparece se rejectCall está ativo */}
              {connectionSettings.rejectCall && (
                <div className="space-y-2 p-4 rounded-lg bg-muted/30 border border-border">
                  <Label htmlFor="msgCall" className="text-base font-medium">
                    Mensagem Personalizada
                  </Label>
                  <p className="text-sm text-muted-foreground mb-2">
                    Esta mensagem será enviada automaticamente após recusar a ligação
                  </p>
                  <Textarea
                    id="msgCall"
                    placeholder="Ex: Olá! No momento não posso atender ligações. Por favor, envie uma mensagem de texto."
                    value={connectionSettings.msgCall}
                    onChange={(e) => setConnectionSettings(prev => ({
                      ...prev,
                      msgCall: e.target.value
                    }))}
                    className="min-h-[100px] resize-none"
                  />
                </div>
              )}

              {/* Botão Salvar */}
              <div className="pt-4 flex justify-end">
                <Button onClick={handleSaveSettings} disabled={savingSettings}>
                  {savingSettings ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    'Salvar Configurações'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Conexão - Escolha QR ou Pairing Code */}
      <Dialog open={connectModalOpen} onOpenChange={(open) => {
        if (!open) handleCancelConnect(connectingConnection?.id);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wifi className="w-5 h-5 text-primary" />
              Conectar WhatsApp
            </DialogTitle>
            <DialogDescription>
              {connectingConnection?.name}
            </DialogDescription>
          </DialogHeader>

          {/* QR Code - vai direto sem escolha */}
          {connectingConnection && (
            <div className="py-4">
              {(() => {
                const qrState = qrStates[connectingConnection.id];
                const hasQR = qrState?.qrImage;
                const isConnected = qrState?.status === 'connected';
                const showLoader = qrState?.showLoader;

                if (isConnected) {
                  return (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                        <Wifi className="w-8 h-8 text-green-500" />
                      </div>
                      <p className="font-medium text-green-500">Conectado!</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Os grupos serão sincronizados automaticamente
                      </p>
                      <Button 
                        className="mt-4" 
                        onClick={() => {
                          setConnectModalOpen(false);
                          fetchConnections(false, ownerFilter);
                        }}
                      >
                        Fechar
                      </Button>
                    </div>
                  );
                }

                return (
                  <div className="flex flex-col items-center">
                    <div className="bg-white rounded-lg p-4">
                      {hasQR ? (
                        <img src={qrState.qrImage} alt="QR Code" className="w-56 h-56" />
                      ) : showLoader ? (
                        <div className="w-56 h-56 flex flex-col items-center justify-center">
                          <Loader2 className="w-10 h-10 animate-spin text-gray-400" />
                          <p className="text-sm text-gray-500 mt-3">Gerando QR Code...</p>
                        </div>
                      ) : (
                        <div className="w-56 h-56 flex items-center justify-center">
                          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-4 text-center">
                      Abra o WhatsApp no celular<br />
                      Vá em <strong>Configurações → Aparelhos conectados</strong><br />
                      Toque em <strong>Conectar um aparelho</strong> e escaneie
                    </p>
                    <Button 
                      variant="ghost" 
                      className="mt-4"
                      onClick={() => handleCancelConnect(connectingConnection.id)}
                    >
                      Cancelar
                    </Button>
                  </div>
                );
              })()}
            </div>
          )}

        </DialogContent>
      </Dialog>
    </div>
  );
}
